// Copyright ©️ 2026 eVolve MEP, LLC

import type { AttachmentFile, FileId, FileMarkup, UpdateMarkupBody } from '@evolvemep/api-ts';

import clearMarkupsSvg from 'assets/icons/icon-src/markup/clear-markups.svg?raw';
import downloadSvg from 'assets/icons/icon-src/markup/download.svg?raw';
import editMarkupSvg from 'assets/icons/icon-src/markup/edit-markup.svg?raw';
import redoSvg from 'assets/icons/icon-src/markup/redo.svg?raw';
import undoSvg from 'assets/icons/icon-src/markup/undo.svg?raw';
import { ArrowTool } from 'components/Autodesk/extensions/MarkupModeExtension/ArrowTool';
import type {
  BaseMarkupTool,
  MarkupToolContext,
} from 'components/Autodesk/extensions/MarkupModeExtension/BaseMarkupTool';
import { CheckmarkTool } from 'components/Autodesk/extensions/MarkupModeExtension/CheckmarkTool';
import { CloudTool } from 'components/Autodesk/extensions/MarkupModeExtension/CloudTool';
import type { ViewerMarkupStoreContextType } from 'components/Autodesk/extensions/MarkupModeExtension/createViewerMarkupStore';
import { CrossMarkTool } from 'components/Autodesk/extensions/MarkupModeExtension/CrossMarkTool';
import { EllipseTool } from 'components/Autodesk/extensions/MarkupModeExtension/EllipseTool';
import { FreehandTool } from 'components/Autodesk/extensions/MarkupModeExtension/FreehandTool';
import { HighlightTool } from 'components/Autodesk/extensions/MarkupModeExtension/HighlightTool';
import { PolycloudTool } from 'components/Autodesk/extensions/MarkupModeExtension/PolycloudTool';
import { PolylineTool } from 'components/Autodesk/extensions/MarkupModeExtension/PolylineTool';
import { PropertiesPanel } from 'components/Autodesk/extensions/MarkupModeExtension/PropertiesPanel';
import { QuestionMarkTool } from 'components/Autodesk/extensions/MarkupModeExtension/QuestionMarkTool';
import { RectangleTool } from 'components/Autodesk/extensions/MarkupModeExtension/RectangleTool';
import { SelectionToolbar } from 'components/Autodesk/extensions/MarkupModeExtension/SelectionToolbar';
import {
  isStampToolType,
  type StampMarkupData,
  type StampToolType,
} from 'components/Autodesk/extensions/MarkupModeExtension/StampMarkup';
import type { StampTool } from 'components/Autodesk/extensions/MarkupModeExtension/StampTool';
import { TextBoxTool } from 'components/Autodesk/extensions/MarkupModeExtension/TextBoxTool';
import { THREE } from 'components/Autodesk/viewerThree';
import type { FilesCacheContextType } from 'components/Mantine/FileUploads/useFileCache';
import { isNotNil } from 'helpers/isNotNil';

const { Autodesk } = window;

import EventPayloadMap = Autodesk.Viewing.Extensions.Markups.Core.EventPayloadMap;
import EditFrameEventPayloadMap = Autodesk.Viewing.Extensions.Markups.Core.EditFrameEventPayloadMap;

const MARKUP_MODE_TOOL_NAME = 'markup-mode-tool';
const DISPLAY_LAYER = 'display-layer';

type MarkupToolOptions = {
  viewer: Autodesk.Viewing.GuiViewer3D;
  markupExtension: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore;
  markupModeExtension: MarkupModeExtension;
};

class MarkupModeTool implements Autodesk.Viewing.ToolInterface {
  // Dependencies
  viewer: Autodesk.Viewing.GuiViewer3D;
  markupExtension: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore;
  markupModeExtension: MarkupModeExtension;
  file: AttachmentFile;

  // UI
  /** Single shared properties panel. Each tool swaps its content in when it activates. */
  propertiesPanel: PropertiesPanel;
  /** Floating action toolbar shown above/below the currently selected markup. */
  selectionToolbar: SelectionToolbar;
  /** Various groups of controls for the main toolbar. */
  controlGroups: Autodesk.Viewing.UI.ControlGroup[] = [];

  // State
  /** Callbacks to clean up event listeners on unload. */
  cleanUpFns: (() => void)[] = [];
  active: boolean = false;
  activeToolType: string | null = null;
  navigating: boolean = false;
  hasUnsavedChanges: boolean = false;
  /** In-session markup edits keyed by page number. Overrides initialMarkupsByPage once set. */
  savedMarkupsByPage: Map<number, string> = new Map();
  /** Initial markup data loaded from the database, keyed by page number. */
  initialMarkupsByPage: Map<number, string> = new Map();
  /** The currently active PDF page number. */
  currentPage: number = 1;

  // Tools
  textBoxTool: TextBoxTool;
  arrowTool: ArrowTool;
  freehandTool: FreehandTool;
  rectangleTool: RectangleTool;
  ellipseTool: EllipseTool;
  cloudTool: CloudTool;
  polycloudTool: PolycloudTool;
  polylineTool: PolylineTool;
  highlightTool: HighlightTool;
  checkmarkTool: CheckmarkTool;
  crossMarkTool: CrossMarkTool;
  questionMarkTool: QuestionMarkTool;
  /** All tools, including stamp tools. Used for generic activate/deactivate dispatch. */
  tools: BaseMarkupTool[] = [];
  /** Only the stamp tools. Used for restore logic. */
  stampTools: StampTool[] = [];

  constructor({ viewer, markupExtension, markupModeExtension }: MarkupToolOptions) {
    this.viewer = viewer;
    this.markupExtension = markupExtension;
    this.markupModeExtension = markupModeExtension;
    this.file = this.markupModeExtension.file;
    const initialMarkupPages = this.file.fileMarkup?.markupPages ?? [];
    for (const { page, markup } of initialMarkupPages) {
      this.initialMarkupsByPage.set(page, markup);
      this.savedMarkupsByPage.set(page, markup);
    }

    this.propertiesPanel = new PropertiesPanel(viewer, 'markup-properties-panel', '', document.createElement('div'));
    this.selectionToolbar = new SelectionToolbar(viewer.container, markupExtension);

    const toolContext: MarkupToolContext = {
      viewer: this.viewer,
      markupExtension,
      beforeActivateTool: this.beforeActivateTool.bind(this),
      propertiesPanel: this.propertiesPanel,
      selectionToolbar: this.selectionToolbar,
    };

    this.textBoxTool = new TextBoxTool(toolContext);
    this.arrowTool = new ArrowTool(toolContext);
    this.freehandTool = new FreehandTool(toolContext);
    this.rectangleTool = new RectangleTool(toolContext);
    this.ellipseTool = new EllipseTool(toolContext);
    this.cloudTool = new CloudTool(toolContext);
    this.polycloudTool = new PolycloudTool(toolContext);
    this.polylineTool = new PolylineTool(toolContext);
    this.highlightTool = new HighlightTool(toolContext);
    this.checkmarkTool = new CheckmarkTool(toolContext);
    this.crossMarkTool = new CrossMarkTool(toolContext);
    this.questionMarkTool = new QuestionMarkTool(toolContext);
    this.stampTools = [this.checkmarkTool, this.crossMarkTool, this.questionMarkTool];
    this.tools = [
      this.textBoxTool,
      this.arrowTool,
      this.freehandTool,
      this.rectangleTool,
      this.ellipseTool,
      this.cloudTool,
      this.polycloudTool,
      this.polylineTool,
      this.highlightTool,
      ...this.stampTools,
    ];
  }

  // ---- ToolInterface methods ----

  getName() {
    return MARKUP_MODE_TOOL_NAME;
  }

  getNames() {
    return [MARKUP_MODE_TOOL_NAME];
  }

  update(_timestamp: number) {
    return false;
  }

  // Tools get called in the order of their priority (higher numbers first). Default tools have a priority of 0.
  getPriority() {
    return 999;
  }

  register() {
    this.trackEventListener(
      this.viewer,
      Autodesk.Viewing.BEFORE_MODEL_UNLOAD_EVENT,
      this.onBeforeModelUnloaded.bind(this),
    );
    this.trackEventListener(
      this.markupExtension,
      Autodesk.Viewing.Extensions.Markups.Core.EVENT_HISTORY_CHANGED,
      this.onHistoryChange.bind(this),
    );
    this.trackEventListener(
      this.markupExtension,
      Autodesk.Viewing.Extensions.Markups.Core.EVENT_MARKUP_SELECTED,
      this.onMarkupSelect.bind(this),
    );
    this.trackEventListener(
      this.markupExtension,
      Autodesk.Viewing.Extensions.Markups.Core.EVENT_MARKUP_DESELECT,
      this.onMarkupDeselect.bind(this),
    );
    this.trackEventListener(
      this.markupExtension.editFrame,
      Autodesk.Viewing.Extensions.Markups.Core.EVENT_EDITFRAME_EDITION_START,
      this.onMarkupEditStart.bind(this),
    );
    this.trackEventListener(
      this.markupExtension.editFrame,
      Autodesk.Viewing.Extensions.Markups.Core.EVENT_EDITFRAME_EDITION_END,
      this.onMarkupEditStop.bind(this),
    );
    this.trackEventListener(this.viewer, Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChange.bind(this));
    this.trackEventListener(this.viewer.container, 'pointerdown', this.onViewerPointerDown.bind(this));
    this.trackEventListener(this.viewer.container, 'scroll', this.onViewerContainerScroll.bind(this));
  }

  deregister() {
    this.cleanUpFns.forEach((fn) => fn());
    this.cleanUpFns = [];
  }

  activate() {
    this.active = true;

    Autodesk.Viewing.Extensions.Markups.Core.Utils.hideLmvUi(this.viewer);

    this.markupExtension.enterEditMode();

    this.controlGroups = this.createControlGroups();
    for (const controlGroup of this.controlGroups) {
      this.viewer.toolbar.addControl(controlGroup);
    }
    this.viewer.toolbar.addClass('markup-toolbar');

    this.activateNavigation();

    this.restoreEditableMarkupsForCurrentPage();
  }

  deactivate() {
    this.active = false;
    if (this.hasUnsavedChanges) {
      this.hasUnsavedChanges = false;
      this.markupModeExtension.viewerMarkupStore.setHasUnsavedChanges(false);
    }
    this.propertiesPanel.setVisible(false);
    this.selectionToolbar.hide();
    for (const controlGroup of this.controlGroups) {
      this.viewer.toolbar.removeControl(controlGroup);
    }
    this.viewer.toolbar.removeClass('markup-toolbar');

    this.deactivateActiveTool();
    this.deactivateNavigation();

    this.displayReadOnlyMarkupForCurrentPage();
  }

  // ---- Event handlers ----

  /** Helper to add event listeners and make sure they get removed when the extension is deregistered. */
  private trackEventListener<
    TType extends string,
    // oxlint-disable-next-line typescript/no-explicit-any
    TCallback extends (...args: any[]) => void,
    TTarget extends {
      addEventListener(type: TType, callback: TCallback): void;
      removeEventListener(type: TType, callback: TCallback): void;
    },
  >(target: TTarget, eventType: TType, callback: TCallback): void {
    target.addEventListener(eventType, callback);
    this.cleanUpFns.push(() => target.removeEventListener(eventType, callback));
  }

  /** Hide markup while the next PDF page is loading. */
  onBeforeModelUnloaded() {
    this.hideMarkup();
  }

  onPageChange({ page }: { page: number }) {
    this.currentPage = page;
    this.displayReadOnlyMarkupForCurrentPage();
  }

  onHistoryChange(event: EventPayloadMap['EVENT_HISTORY_CHANGED']) {
    const { action } = event.data;
    const { undoStack, redoStack } = event.target;
    // Note: we're avoiding event.target.getLastElementInUndoStack() as it can throw an error when entering edit mode.
    const lastElementInUndoStack = undoStack[undoStack.length - 1];
    const lastElementInRedoStack = redoStack[redoStack.length - 1];
    if (action === 'undo' || action === 'redo') {
      const selection = this.markupExtension.getSelection();
      if (selection) this.selectionToolbar.reposition(selection);
      // Update the properties panel when an undo/redo action would cause the selected markup to change.
      const actionGroup = action === 'undo' ? lastElementInRedoStack : lastElementInUndoStack;
      const hasSetStyleEvent =
        actionGroup?.actions.some((editAction) => editAction.addToHistory && editAction.type === 'SET-STYLE') ?? false;
      if (hasSetStyleEvent) {
        const markup = this.markupExtension.getMarkup(event.data.targetId);
        if (markup) this.tools.find((tool) => tool.type === markup.type)?.updatePropertyPanelForStyle(markup.style);
      }
    }
    const canUndo = undoStack.length > 0;
    const canRedo = redoStack.length > 0;
    const historyToolsControlGroup = this.viewer.toolbar.getControl(
      'markup-historyTools',
    ) as Autodesk.Viewing.UI.ControlGroup | null;
    this.setHasUnsavedChanges(
      this.hasUnsavedChanges ||
        (lastElementInUndoStack?.actions.some((editAction) => editAction.addToHistory) ?? false),
    );
    const clearButton = historyToolsControlGroup?.getControl(
      'toolbar-markup-clear',
    ) as Autodesk.Viewing.UI.Button | null;
    const undoButton = historyToolsControlGroup?.getControl('toolbar-markup-undo') as Autodesk.Viewing.UI.Button | null;
    const redoButton = historyToolsControlGroup?.getControl('toolbar-markup-redo') as Autodesk.Viewing.UI.Button | null;
    clearButton?.setState(
      this.markupExtension.markups.length > 0
        ? Autodesk.Viewing.UI.Button.State.INACTIVE
        : Autodesk.Viewing.UI.Button.State.DISABLED,
    );
    undoButton?.setState(
      canUndo ? Autodesk.Viewing.UI.Button.State.INACTIVE : Autodesk.Viewing.UI.Button.State.DISABLED,
    );
    redoButton?.setState(
      canRedo ? Autodesk.Viewing.UI.Button.State.INACTIVE : Autodesk.Viewing.UI.Button.State.DISABLED,
    );
  }

  onMarkupSelect(event: EventPayloadMap['EVENT_MARKUP_SELECTED']) {
    const { markup } = event;
    this.activateTool(markup.type);
    this.tools.find((tool) => tool.type === markup.type)?.onMarkupSelected();
    this.selectionToolbar.show(markup);
  }

  onMarkupDeselect(_event: EventPayloadMap['EVENT_MARKUP_DESELECT']) {
    this.selectionToolbar.hide();
  }

  /** Callback invoked when the selected markup is being edited, i.e. dragging, resizing, or rotating the markup. */
  onMarkupEditStart(_event: EditFrameEventPayloadMap['EVENT_EDITFRAME_EDITION_START']) {
    this.selectionToolbar.hide();
  }

  /** Callback invoked when the selected markup is no longer being edited. */
  onMarkupEditStop(event: EditFrameEventPayloadMap['EVENT_EDITFRAME_EDITION_END']) {
    const markup = event.target.markup;
    if (isNotNil(markup)) this.selectionToolbar.show(markup);
  }

  onCameraChange() {
    if (!this.selectionToolbar.visible) return;
    const markup = this.markupExtension.getSelection();
    if (isNotNil(markup)) this.selectionToolbar.reposition(markup);
  }

  /**
   * If the properties panel has focus and the user clicks in the viewer outside of it, blur any input.
   * The viewer is swallowing pointer events, so this guarantees that the input gets blurred when the user
   * clicks anywhere else in the viewer.
   */
  onViewerPointerDown(event: MouseEvent) {
    const target = event.target as Node | null;
    if (
      !this.propertiesPanel.container.contains(target) &&
      this.propertiesPanel.container.contains(document.activeElement)
    ) {
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }

  /**
   * When the text input helper is active and the browser would scroll the viewer container
   * to keep the text cursor visible, reset the DOM scroll and pan the viewer instead.
   */
  onViewerContainerScroll() {
    const textInputHelper = this.markupExtension.editMode.textInputHelper;
    if (!textInputHelper?.isActive()) return;

    const container = this.viewer.container;
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    if (scrollLeft === 0 && scrollTop === 0) return;

    // Reset the DOM scroll so the canvas doesn't visually shift.
    container.scrollLeft = 0;
    container.scrollTop = 0;

    // Move the camera to show the text, plus a buffer.
    const offset = 16;
    const deltaX = scrollLeft > 0 ? scrollLeft + offset : 0;
    const deltaY = scrollTop > 0 ? scrollTop + offset : 0;
    this.panViewerByPixels(deltaX, deltaY);
  }

  /**
   * Translates the viewer camera by a specified number of pixels along each axis.
   */
  private panViewerByPixels(deltaX: number, deltaY: number) {
    // Their camera is orthographic but typed as Camera.
    const camera = this.viewer.impl.camera as THREE.OrthographicCamera;
    const containerWidth = this.viewer.container.clientWidth;
    const containerHeight = this.viewer.container.clientHeight;

    const worldWidth = camera.right - camera.left;
    const worldHeight = camera.top - camera.bottom;

    const cameraDeltaX = (deltaX / containerWidth) * worldWidth;
    // Negate deltaY as scrolling down in the DOM moves the camera up in the viewer (y-axis is inverted).
    const cameraDeltaY = -(deltaY / containerHeight) * worldHeight;

    const navigation = this.viewer.navigation;
    const position: THREE.Vector3 = navigation.getPosition().clone();
    const target: THREE.Vector3 = navigation.getTarget().clone();
    position.x += cameraDeltaX;
    position.y += cameraDeltaY;
    target.x += cameraDeltaX;
    target.y += cameraDeltaY;
    navigation.setView(position, target);
  }

  // ---- Markup Display ----

  hideMarkup() {
    this.markupExtension.unloadMarkupsAllLayers();
  }

  displayReadOnlyMarkupForCurrentPage() {
    this.markupExtension.unloadMarkupsAllLayers();
    this.markupExtension.show();
    this.markupExtension.leaveEditMode();
    const displayMarkup = this.savedMarkupsByPage.get(this.currentPage);
    if (displayMarkup) {
      this.markupExtension.loadMarkups(displayMarkup, DISPLAY_LAYER);
    }
    Autodesk.Viewing.Extensions.Markups.Core.Utils.restoreLmvUi(this.viewer);
  }

  restoreEditableMarkupsForCurrentPage() {
    /**
     * NOTE: The markup extension doesn't support loading custom markups out of the box (they'll
     * just be rendered as non-editable SVGs), so we manually extract them from the SVG string,
     * load the native markups, then re-create the custom stamps.
     *
     * SVG structure:
     * <svg>
     *   <metadata><markup_document data-model-version="4" /></metadata>
     *   <g> <!-- one per markup element -->
     *     <metadata><markup_element type="arrow|checkmark|crossMark|…" /></metadata>
     *     …svg elements…
     *   </g>
     * </svg>
     */
    const svgString = this.savedMarkupsByPage.get(this.currentPage) ?? '';

    this.markupExtension.unloadMarkupsAllLayers();
    this.markupExtension.leaveEditMode();
    this.markupExtension.show();

    const stampEditModesByTypeMap = new Map(this.stampTools.map((tool) => [tool.type, tool.createEditMode()]));

    // Parse the saved SVG and extract custom stamp elements.
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgRoot = svgDoc.documentElement;

    const pendingStamps: { markupType: StampToolType; data: StampMarkupData }[] = [];

    // Remove custom markup elements from the markup SVG.
    for (const gElement of Array.from(svgRoot.querySelectorAll('g'))) {
      const metaElement = gElement.querySelector('metadata markup_element');
      const markupType = metaElement?.getAttribute('type');
      if (!markupType || !isStampToolType(markupType)) continue;
      const editMode = stampEditModesByTypeMap.get(markupType);
      const data = editMode?.getMarkupFromSvg(gElement);
      if (data) {
        pendingStamps.push({ markupType, data });
        gElement.parentNode?.removeChild(gElement);
      }
    }

    // Load native markups (stripped of our custom elements).
    const strippedSvg = new XMLSerializer().serializeToString(svgRoot);
    this.markupExtension.unloadMarkupsAllLayers();
    this.markupExtension.loadMarkups(strippedSvg, DISPLAY_LAYER);
    this.markupExtension.enterEditMode(DISPLAY_LAYER);

    // Create a markup element for each custom stamp via its edit mode.
    for (const stamp of pendingStamps) {
      const editMode = stampEditModesByTypeMap.get(stamp.markupType);
      editMode?.createMarkup(stamp.data, false);
    }

    // enterEditMode() turns off navigation, so if we were navigating before, turn it back on.
    if (this.navigating) {
      this.markupExtension.allowNavigation(true);
    }

    // Update the state of the clear button once markups have been loaded.
    const historyToolsControlGroup = this.viewer.toolbar.getControl(
      'markup-historyTools',
    ) as Autodesk.Viewing.UI.ControlGroup | null;
    const clearButton = historyToolsControlGroup?.getControl(
      'toolbar-markup-clear',
    ) as Autodesk.Viewing.UI.Button | null;
    clearButton?.setState(
      this.markupExtension.markups.length > 0
        ? Autodesk.Viewing.UI.Button.State.INACTIVE
        : Autodesk.Viewing.UI.Button.State.DISABLED,
    );
  }

  /**
   * Gets the saved markup for the current page with any necessary metadata appended, formatted as an SVG+XML string.
   */
  getSavedMarkupForCurrentPage() {
    const generatedMarkup = this.markupExtension.generateData();

    // Get the bounds to determine the document's dimensions in inches.
    const bounds = this.viewer.model.getBoundingBox();

    // Get the document's position in the client represented in pixels.
    const topLeft = this.viewer.impl.worldToClient(new THREE.Vector3(bounds.min.x, bounds.max.y, 0));
    const bottomRight = this.viewer.impl.worldToClient(new THREE.Vector3(bounds.max.x, bounds.min.y, 0));

    const docOffsetLeftInPx = topLeft.x;
    const docOffsetTopInPx = topLeft.y;
    const docWidthInPx = bottomRight.x - topLeft.x;

    const docWidthInInches = bounds.max.x - bounds.min.x;
    const docHeightInInches = bounds.max.y - bounds.min.y;
    const pixelsPerInch = docWidthInPx / docWidthInInches;
    const docOffsetLeftInInches = docOffsetLeftInPx / pixelsPerInch;
    const docOffsetTopInInches = docOffsetTopInPx / pixelsPerInch;

    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(generatedMarkup, 'image/svg+xml');
    const svgElement = svgDocument.documentElement;

    // Store the document's offset relative to the canvas to its metadata. This lets us position the markup properly during file downloads.
    const documentMetadataElement = svgElement.querySelector('metadata markup_document');
    documentMetadataElement?.setAttribute('data-document-offset-left-in-inches', docOffsetLeftInInches.toString());
    documentMetadataElement?.setAttribute('data-document-offset-top-in-inches', docOffsetTopInInches.toString());
    documentMetadataElement?.setAttribute('data-document-width-in-inches', docWidthInInches.toString());
    documentMetadataElement?.setAttribute('data-document-height-in-inches', docHeightInInches.toString());

    // Remove any <parsererror> elements. These can be added if a user tries to save without any markup drawn.
    svgElement.querySelectorAll('parsererror').forEach((parseErrorElement) => {
      parseErrorElement.parentNode?.removeChild(parseErrorElement);
    });

    // Return the markup string.
    return new XMLSerializer().serializeToString(svgElement);
  }

  // ---- Tool management ----

  beforeActivateTool(toolType: string) {
    if (toolType === this.activeToolType) return;
    this.deactivateNavigation();
    this.deactivateActiveTool();
    this.activeToolType = toolType;
  }

  activateTool(toolType: string) {
    this.tools.find((tool) => tool.type === toolType)?.activate();
  }

  deactivateActiveTool() {
    if (!this.activeToolType) return;
    this.tools.find((tool) => tool.type === this.activeToolType)?.deactivate();
    this.activeToolType = null;
  }

  activateNavigation() {
    if (this.navigating) return;
    this.navigating = true;
    // Handle case where user is editing text when they press the navigation button.
    this.textBoxTool.saveChangesIfEditing();
    this.deactivateActiveTool();
    this.markupExtension.allowNavigation(true);
    const navigationToolsControlGroup = this.viewer.toolbar.getControl(
      'markup-navigationTools',
    ) as Autodesk.Viewing.UI.ControlGroup | null;
    const navigateButton = navigationToolsControlGroup?.getControl(
      'toolbar-markup-navigate',
    ) as Autodesk.Viewing.UI.Button | null;
    navigateButton?.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
    this.propertiesPanel.setVisible(false);
  }

  deactivateNavigation() {
    if (!this.navigating) return;
    this.navigating = false;
    this.markupExtension.allowNavigation(false);
    const navigationToolsControlGroup = this.viewer.toolbar.getControl(
      'markup-navigationTools',
    ) as Autodesk.Viewing.UI.ControlGroup | null;
    const navigateButton = navigationToolsControlGroup?.getControl(
      'toolbar-markup-navigate',
    ) as Autodesk.Viewing.UI.Button | null;
    navigateButton?.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
  }

  setHasUnsavedChanges(hasUnsavedChanges: boolean) {
    if (this.hasUnsavedChanges === hasUnsavedChanges) return;
    this.hasUnsavedChanges = hasUnsavedChanges;
    // Used to block certain actions in the UI unless the user confirms they want to discard their changes.
    this.markupModeExtension.viewerMarkupStore.setHasUnsavedChanges(hasUnsavedChanges);
    const documentToolsControlGroup = this.viewer.toolbar.getControl(
      'markup-documentTools',
    ) as Autodesk.Viewing.UI.ControlGroup | null;
    const saveButton = documentToolsControlGroup?.getControl(
      'toolbar-markup-save',
    ) as Autodesk.Viewing.UI.Button | null;
    const state = this.hasUnsavedChanges
      ? Autodesk.Viewing.UI.Button.State.ACTIVE
      : Autodesk.Viewing.UI.Button.State.DISABLED;
    saveButton?.setState(state);
  }

  // ---- Toolbar ----

  createControlGroups() {
    return [
      this.createNavigationControlGroup(),
      this.createTextControlGroup(),
      this.createDrawingControlGroup(),
      this.createShapeControlGroup(),
      this.createStampControlGroup(),
      this.createHistoryControlGroup(),
      this.createDocumentControlGroup(),
      this.createSettingsControlGroup(),
    ];
  }

  private createNavigationControlGroup() {
    const navigateButton = new Autodesk.Viewing.UI.Button('toolbar-markup-navigate');
    navigateButton.onClick = () => {
      this.activateNavigation();
    };
    navigateButton.icon.classList.add('adsk-button-icon', 'adsk-icon-pan');
    navigateButton.setToolTip('Navigate');

    const group = new Autodesk.Viewing.UI.ControlGroup('markup-navigationTools');
    group.addControl(navigateButton);
    return group;
  }

  private createTextControlGroup() {
    const group = new Autodesk.Viewing.UI.ControlGroup('markup-textTools');
    group.addControl(this.textBoxTool.createButton());
    return group;
  }

  private createDrawingControlGroup() {
    const group = new Autodesk.Viewing.UI.ControlGroup('markup-drawingTools');
    group.addControl(this.freehandTool.createButton());
    group.addControl(this.highlightTool.createButton());
    return group;
  }

  private createShapeControlGroup() {
    const group = new Autodesk.Viewing.UI.ControlGroup('markup-shapeTools');
    group.addControl(this.arrowTool.createButton());
    group.addControl(this.rectangleTool.createButton());
    group.addControl(this.ellipseTool.createButton());
    group.addControl(this.cloudTool.createButton());
    group.addControl(this.polylineTool.createButton());
    group.addControl(this.polycloudTool.createButton());
    return group;
  }

  private createStampControlGroup() {
    const group = new Autodesk.Viewing.UI.ControlGroup('markup-stampTools');
    group.addControl(this.checkmarkTool.createButton());
    group.addControl(this.questionMarkTool.createButton());
    group.addControl(this.crossMarkTool.createButton());
    return group;
  }

  /** Clears all markups as a single revertible action group (undo/redo supported). */
  clearMarkups() {
    const markups = this.markupExtension.markups;
    if (markups.length === 0) return;
    // Deselect any active markup so the selection toolbar/properties panel don't reference a deleted markup.
    this.markupExtension.selectMarkup(null);
    // Group all individual delete actions so a single undo restores every markup at once.
    this.markupExtension.beginActionGroup();
    markups.forEach((markup) => {
      const editMode = markup.getEditMode();
      if (!editMode) return;
      this.markupExtension.editMode = editMode;
      this.markupExtension.editMode.deleteMarkup(markup);
    });
    this.markupExtension.closeActionGroup();
  }

  private createHistoryControlGroup() {
    const clearButton = new Autodesk.Viewing.UI.Button('toolbar-markup-clear');
    clearButton.onClick = () => {
      this.clearMarkups();
    };
    clearButton.icon.innerHTML = clearMarkupsSvg;
    clearButton.setToolTip('Clear markups');

    const undoButton = new Autodesk.Viewing.UI.Button('toolbar-markup-undo');
    undoButton.onClick = () => {
      this.markupExtension.undo();
    };
    undoButton.icon.innerHTML = undoSvg;
    undoButton.addClass('custom-icon-toolbar-button');
    undoButton.setToolTip('Undo');
    undoButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);

    const redoButton = new Autodesk.Viewing.UI.Button('toolbar-markup-redo');
    redoButton.onClick = () => {
      this.markupExtension.redo();
    };
    redoButton.icon.innerHTML = redoSvg;
    redoButton.addClass('custom-icon-toolbar-button');
    redoButton.setToolTip('Redo');
    redoButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);

    const group = new Autodesk.Viewing.UI.ControlGroup('markup-historyTools');
    group.addControl(clearButton);
    group.addControl(undoButton);
    group.addControl(redoButton);
    return group;
  }

  private createDocumentControlGroup() {
    const saveButton = new Autodesk.Viewing.UI.Button('toolbar-markup-save');
    saveButton.addClass('adsk-label-button');
    saveButton.icon.style.setProperty('display', 'none');
    const saveButtonLabel = document.createElement('label');
    saveButtonLabel.innerText = 'Save';
    saveButton.container.appendChild(saveButtonLabel);
    saveButton.setToolTip('Save');
    saveButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);
    saveButton.onClick = async () => {
      this.markupExtension.selectMarkup(null);
      // Handle case where user is editing text when they try to save their markup.
      this.textBoxTool.saveChangesIfEditing();
      const markup = this.getSavedMarkupForCurrentPage();
      saveButton.setState(Autodesk.Viewing.UI.Button.State.DISABLED);
      const label = saveButton.container.querySelector('label') ?? document.createElement('label');
      label.innerText = 'Saving...';
      this.savedMarkupsByPage.set(this.currentPage, markup);
      // Save changes to the database.
      await this.markupModeExtension.updateMarkup(this.file.fileId, {
        page: this.currentPage,
        markup,
      });
      label.innerText = 'Save';
      this.setHasUnsavedChanges(false);
    };

    const exitButton = new Autodesk.Viewing.UI.Button('toolbar-markup-exit');
    exitButton.addClass('adsk-label-button');
    exitButton.addClass('label-button-outline');
    exitButton.icon.style.setProperty('display', 'none');
    const exitButtonLabel = document.createElement('label');
    exitButtonLabel.innerText = 'Exit';
    exitButton.container.appendChild(exitButtonLabel);
    exitButton.setToolTip('Exit');
    exitButton.onClick = () => {
      if (!this.hasUnsavedChanges) {
        this.deactivate();
        return;
      }
      this.markupModeExtension.viewerMarkupStore.setPromptAction(() => this.deactivate());
    };

    const group = new Autodesk.Viewing.UI.ControlGroup('markup-documentTools');
    group.addControl(saveButton);
    group.addControl(exitButton);
    return group;
  }

  private createSettingsControlGroup() {
    const fullScreenButton = new Autodesk.Viewing.UI.Button('toolbar-fullscreen');
    const minimizedMode = 0;
    // const mysteryMode = 1;
    const fullScreenMode = 2;
    const initialIsFullScreen = this.viewer.screenModeDelegate.getMode() === fullScreenMode;
    const fullScreenTooltip = initialIsFullScreen ? 'Exit Full Screen' : 'Full Screen';
    const fullScreenIconClass = initialIsFullScreen ? 'adsk-icon-fullscreen-exit' : 'adsk-icon-fullscreen';
    fullScreenButton.icon.classList.add('adsk-button-icon', fullScreenIconClass);
    fullScreenButton.setToolTip(fullScreenTooltip);
    fullScreenButton.onClick = () => {
      const isFullScreen = this.viewer.screenModeDelegate.getMode() === fullScreenMode;
      if (isFullScreen) {
        this.viewer.screenModeDelegate.setMode(minimizedMode);
        fullScreenButton.icon.classList.remove('adsk-icon-fullscreen-exit');
        fullScreenButton.icon.classList.add('adsk-icon-fullscreen');
        fullScreenButton.setToolTip('Full Screen');
      } else {
        this.viewer.screenModeDelegate.setMode(fullScreenMode);
        fullScreenButton.icon.classList.remove('adsk-icon-fullscreen');
        fullScreenButton.icon.classList.add('adsk-icon-fullscreen-exit');
        fullScreenButton.setToolTip('Exit Full Screen');
      }
    };

    const group = new Autodesk.Viewing.UI.ControlGroup('markup-settingsTools');
    group.addControl(fullScreenButton);
    return group;
  }
}

type MarkupExtensionOptions = Autodesk.Viewing.ExtensionOptions & {
  /** Used to load and persist markup data for files if `markupEnabled` is true. */
  file: AttachmentFile;
  /** Callback used to persist markup data for files if `markupEnabled` is true. */
  updateMarkup: (fileId: FileId, body: UpdateMarkupBody) => Promise<FileMarkup>;
  /** Callback to download a file, optionally with markup. */
  downloadFile: FilesCacheContextType['downloadFile'];
  /** Per-viewer store — used to communicate unsaved-changes state and pending discard confirmations. */
  viewerMarkupStore: ViewerMarkupStoreContextType;
};

export class MarkupModeExtension extends Autodesk.Viewing.Extension {
  controlGroup?: Autodesk.Viewing.UI.ControlGroup;
  markupTool?: MarkupModeTool;
  /** Tracks whether the UI has been created to avoid creating it multiple times if we get multiple TOOLBAR_CREATED_EVENT events. */
  created: boolean = false;
  file: AttachmentFile;
  updateMarkup: (fileId: FileId, body: UpdateMarkupBody) => Promise<FileMarkup>;
  downloadFile: FilesCacheContextType['downloadFile'];
  viewerMarkupStore: ViewerMarkupStoreContextType;

  constructor(
    viewer: Autodesk.Viewing.GuiViewer3D,
    { file, updateMarkup, downloadFile, viewerMarkupStore, ...options }: MarkupExtensionOptions,
  ) {
    super(viewer, options);
    this.file = file;
    this.downloadFile = downloadFile;
    this.updateMarkup = updateMarkup;
    this.viewerMarkupStore = viewerMarkupStore;
  }

  async load() {
    if (isNotNil(this.viewer.toolbar)) {
      // Toolbar is already available, create the UI.
      await this.createUI();
    } else {
      // Toolbar hasn't been created yet, wait until we get notification of its creation.
      this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.onToolbarCreated.bind(this), {
        once: true,
      });
    }

    return true;
  }

  unload() {
    this.viewerMarkupStore.clear();
    this.viewer.unloadExtension('Autodesk.Viewing.MarkupsCore');
    this.viewer.toolController.deactivateTool(MARKUP_MODE_TOOL_NAME);
    if (this.markupTool) {
      this.markupTool.selectionToolbar.destroy();
      this.viewer.toolController.deregisterTool(this.markupTool);
    }
    if (this.controlGroup) this.viewer.toolbar.removeControl(this.controlGroup);

    return true;
  }

  async onToolbarCreated() {
    await this.createUI();
  }

  createDownloadPopover() {
    const downloadWithMarkupsButton = new Autodesk.Viewing.UI.Button('toolbar-downloadWithMarkups');
    downloadWithMarkupsButton.addClass('adsk-label-button');
    downloadWithMarkupsButton.addClass('tooltip-label-button');
    downloadWithMarkupsButton.icon.style.setProperty('display', 'none');
    const downloadWithMarkupsButtonLabel = document.createElement('label');
    downloadWithMarkupsButtonLabel.innerText = 'Download with markups';
    downloadWithMarkupsButton.container.appendChild(downloadWithMarkupsButtonLabel);
    downloadWithMarkupsButton.onClick = () => {
      void this.downloadFile(this.file.fileId);
    };

    const downloadWithoutMarkupsButton = new Autodesk.Viewing.UI.Button('toolbar-downloadWithoutMarkups');
    downloadWithoutMarkupsButton.addClass('adsk-label-button');
    downloadWithoutMarkupsButton.addClass('tooltip-label-button');
    downloadWithoutMarkupsButton.icon.style.setProperty('display', 'none');
    const downloadWithoutMarkupsButtonLabel = document.createElement('label');
    downloadWithoutMarkupsButtonLabel.innerText = 'Download without markups';
    downloadWithoutMarkupsButton.container.appendChild(downloadWithoutMarkupsButtonLabel);
    downloadWithoutMarkupsButton.onClick = () => {
      void this.downloadFile(this.file.fileId, { excludeMarkup: true });
    };

    const downloadPopover = new Autodesk.Viewing.UI.ComboButton('toolbar-download');
    downloadPopover.icon.innerHTML = downloadSvg;
    downloadPopover.addClass('custom-icon-toolbar-button');
    downloadPopover.addControl(downloadWithMarkupsButton);
    downloadPopover.addControl(downloadWithoutMarkupsButton);

    return downloadPopover;
  }

  notifyPageChange(page: number) {
    this.markupTool?.onPageChange({ page });
  }

  async createUI() {
    if (this.created) return;

    this.created = true;
    // NOTE: We're waiting to register these extensions until the toolbar and viewer.toolController are available.
    await this.viewer.loadExtension('Autodesk.Viewing.MarkupsCore');
    const markupExtension = this.viewer.getExtension('Autodesk.Viewing.MarkupsCore');
    this.markupTool = new MarkupModeTool({
      viewer: this.viewer,
      markupExtension,
      markupModeExtension: this,
    });

    this.controlGroup = new Autodesk.Viewing.UI.ControlGroup('fileTools');

    const markupModeButton = new Autodesk.Viewing.UI.Button('toolbar-editMarkup');
    const toolName = this.markupTool.getName();
    markupModeButton.onClick = () => {
      this.viewer.toolController.activateTool(toolName);
    };
    markupModeButton.icon.innerHTML = editMarkupSvg;
    markupModeButton.addClass('custom-icon-toolbar-button');
    markupModeButton.setToolTip('Edit Markup');

    const downloadPopover = this.createDownloadPopover();

    this.controlGroup.addControl(markupModeButton);
    this.controlGroup.addControl(downloadPopover);

    this.viewer.toolController.registerTool(this.markupTool);
    this.viewer.toolbar.addControl(this.controlGroup, { index: 1 });
    this.markupTool.displayReadOnlyMarkupForCurrentPage();
  }
}
